import { StoryCard, IssueTag, Playlist, UserSongEngagement } from './types';

export function sortHomeCards(cards: StoryCard[], userInterests?: string[]): StoryCard[] {
  return [...cards].sort((a, b) => {
    // If user has interests, prioritize songs matching those interests
    if (userInterests && userInterests.length > 0) {
      const aMatchesInterests = a.tags.some(tag => userInterests.includes(tag));
      const bMatchesInterests = b.tags.some(tag => userInterests.includes(tag));

      if (aMatchesInterests && !bMatchesInterests) return -1;
      if (!aMatchesInterests && bMatchesInterests) return 1;

      // If both match, count how many interests they match
      if (aMatchesInterests && bMatchesInterests) {
        const aMatches = a.tags.filter(tag => userInterests.includes(tag)).length;
        const bMatches = b.tags.filter(tag => userInterests.includes(tag)).length;
        if (aMatches !== bMatches) return bMatches - aMatches;
      }
    }

    // Then sort by explicit order if set
    if (a.order != null && b.order != null) return a.order - b.order;
    if (a.order != null) return -1;
    if (b.order != null) return 1;

    // Then by date
    const aDate = a.createdAt ?? '';
    const bDate = b.createdAt ?? '';
    if (aDate && bDate && aDate !== bDate) return aDate.localeCompare(bDate);

    // Finally alphabetically
    return a.songTitle.localeCompare(b.songTitle);
  });
}

export function sortFilteredCards(cards: StoryCard[], tagFilter: IssueTag): StoryCard[] {
  const homeSorted = sortHomeCards(cards);

  const filtered = homeSorted.filter(song => song.tags.includes(tagFilter));

  return filtered.sort((a, b) => {
    const aPrimary = a.tags[0] === tagFilter;
    const bPrimary = b.tags[0] === tagFilter;

    if (aPrimary && !bPrimary) return -1;
    if (!aPrimary && bPrimary) return 1;

    if (a.order != null && b.order != null && a.order !== b.order) {
      return a.order - b.order;
    }

    return a.songTitle.localeCompare(b.songTitle);
  });
}

export function sortSavedCardsByEngagement(
  cards: StoryCard[],
  engagements: UserSongEngagement[]
): StoryCard[] {
  const engagementMap = new Map<string, string>();
  engagements.forEach(e => {
    if (e.saved) {
      engagementMap.set(e.songId, e.lastEngagedAt);
    }
  });

  return [...cards]
    .sort((a, b) => {
      const aTime = engagementMap.get(a.id) ?? '';
      const bTime = engagementMap.get(b.id) ?? '';
      // Sort by engagement time descending (most recent first)
      // Cards without engagement (empty string) will sort to the end
      return bTime.localeCompare(aTime);
    });
}

export function sortPlaylists(playlists: Playlist[], userInterests?: string[]): Playlist[] {
  return [...playlists].sort((a, b) => {
    // If user has interests, prioritize playlists matching those interests
    if (userInterests && userInterests.length > 0) {
      // Check if playlist name or description contains any of the user's interests
      const aMatchesInterests = userInterests.some(interest => {
        const lowerInterest = interest?.toLowerCase() || '';
        const aName = a.name?.toLowerCase() || '';
        const aDesc = a.description?.toLowerCase() || '';
        return aName.includes(lowerInterest) || aDesc.includes(lowerInterest);
      });
      const bMatchesInterests = userInterests.some(interest => {
        const lowerInterest = interest?.toLowerCase() || '';
        const bName = b.name?.toLowerCase() || '';
        const bDesc = b.description?.toLowerCase() || '';
        return bName.includes(lowerInterest) || bDesc.includes(lowerInterest);
      });

      if (aMatchesInterests && !bMatchesInterests) return -1;
      if (!aMatchesInterests && bMatchesInterests) return 1;
    }

    // Then sort by playlist order
    return a.playlistOrder - b.playlistOrder;
  });
}
